// lookup of short codes used in URLs to their longer equivalents
export const urlParamCodes = {
  ct: 'census_tracts',
  co: 'counties',
  s: 'states',
  n: 'conus',
  bs: 'by_state',
  bc: 'by_county',
  bp: 'by_place',
  bpc: 'by_place_county',
}

export const toURLParamCode = {
  census_tracts: 'ct',
  counties: 'co',
  states: 's',
  conus: 'n',
  by_state: 'bs',
  by_county: 'bc',
  by_place: 'bp',
  by_place_county: 'bpc',
}

// nested structure of reportingUnits, areaType, compareArea
export const areaParams = {
  census_tracts: {
    states: {
      by_state: {
        label: 'This state',
        guidance:
          'Choose this option to see how Census tracts compare to each other within this state.',
      },
      conus: {
        label: 'Nation',
        guidance:
          'Choose this option to see how Census tracts compare to all Census tracts in the contiguous U.S.',
      },
    },
    counties: {
      by_county: {
        label: 'This county',
        guidance:
          'Choose this option to see how Census tracts compare to each other within this county.  Note: if there are only a few Census tracts in this community, you may get more useful results at the state level.',
      },
      by_state: {
        label: 'State',
        guidance:
          'Choose this option to see how Census tracts compare to each other within this state.',
      },
      conus: {
        label: 'Nation',
        guidance:
          'Choose this option to see how Census tracts compare to all Census tracts in the contiguous U.S.',
      },
    },
    places: {
      by_place: {
        label: 'This community',
        guidance:
          'Choose this option to see how Census tracts compare to each other within this community.  Note: if there are only a few Census tracts in this community, you may get more useful results at the county level.',
      },
      by_place_county: {
        label: 'County',
        guidance:
          'Choose this option to see how Census tracts compare to each other within all Census tracts within all counties that overlap with this community.',
      },
      by_state: {
        label: 'State',
        guidance:
          'Choose this option to see how Census tracts compare to each other within this state.',
      },
      conus: {
        label: 'Nation',
        guidance:
          'Choose this option to see how Census tracts compare to all Census tracts in the contiguous U.S.',
      },
    },
  },

  counties: {
    states: {
      by_state: {
        label: 'State',
        guidance:
          'Choose this option to see how counties compare to each other within this state.',
      },
      conus: {
        label: 'Nation',
        guidance:
          'Choose this option to see how counties compare to all counties in the contiguous U.S.',
      },
    },
    counties: {
      conus: {
        label: 'Nation',
        guidance:
          'Choose this option to see how counties compare to all counties in the contiguous U.S.',
      },
    },
  },
}

export const defaultCompareArea = {
  census_tracts: {
    states: 'by_state',
    counties: 'by_state',
    places: 'by_place_county',
  },
  counties: {
    states: 'by_state',
    // other options not valid
  },
}

/**
 * Get appropriate compareArea for target areaType
 * @param {String} reportingUnits - current reporting units
 * @param {String} compareArea - current compareArea
 * @param {String} areaType - target areaType
 */
export const getTransitionCompareArea = (
  reportingUnits,
  compareArea,
  areaType
) => {
  if (
    areaParams[reportingUnits] &&
    areaParams[reportingUnits][areaType] &&
    areaParams[reportingUnits][areaType][compareArea]
  ) {
    return compareArea
  }

  return defaultCompareArea[reportingUnits][areaType]
}

/**
 * Validate area parameters
 * @param {String} reportingUnits
 *  * @param {String} areaType
 * @param {String} compareArea
 * @returns true if valid
 */
export const validateAreaParams = (reportingUnits, areaType, compareArea) => {
  if (!areaParams[reportingUnits]) {
    console.error(`ERROR: invalid reportingUnits: ${reportingUnits}`)
    return false
  }

  if (!areaParams[reportingUnits][areaType]) {
    console.error(
      `ERROR: invalid areaType: ${areaType} for reportingUnits: ${reportingUnits}`
    )
    return false
  }

  if (!areaParams[reportingUnits][areaType][compareArea]) {
    console.error(
      `ERROR: invalid compareArea: ${compareArea} for reportingUnits: ${reportingUnits}, areaType: ${areaType}`
    )
    return false
  }

  return true
}

export const getCompareAreaOptions = (
  reportingUnits,
  areaType,
  hasMultipleCounties = false
) => {
  if (areaType === 'places') {
    return Object.entries(areaParams[reportingUnits].places).map(
      ([value, { label, ...rest }]) => ({
        ...rest,
        value,
        label:
          value === 'by_county' && hasMultipleCounties > 1
            ? 'Overlapping counties'
            : label,
      })
    )
  }

  return Object.entries(areaParams[reportingUnits][areaType]).map(
    ([value, rest]) => ({ value, ...rest })
  )
}

export const reportingUnitsLabel = {
  census_tracts: 'Census tracts',
  counties: 'counties',
}

/**
 * Use the length of a Census GEOID based area ID to determine the type of the area
 * @param {String} areaId
 * @returns one of: 'states', 'counties', 'places', 'census_tracts'
 */
export const getAreaType = (areaId) => {
  switch (areaId.length) {
    case 2: {
      return 'states'
    }
    case 5: {
      return 'counties'
    }
    case 7:
    case 10: {
      // 7 are census designated places, 10 are county subdivisions
      return 'places'
    }
    case 11: {
      return 'census_tracts'
    }

    default: {
      throw new Error(`ERROR: invalid areaId: ${areaId}`)
    }
  }
}

export const getStateId = (id) => (id.length === 2 ? id : id.slice(0, 2))

export const getCompareAreaLabel = (
  compareArea,
  stateName,
  areaName = null,
  hasMultipleCounties = false
) => {
  switch (compareArea) {
    case 'by_place': {
      return `${areaName}, ${stateName}`
    }
    case 'by_place_county': {
      return hasMultipleCounties ? 'overlapping counties' : areaName
    }
    case 'by_county': {
      return areaName
    }
    case 'by_state': {
      return stateName
    }
    case 'conus': {
      return 'contiguous U.S.'
    }
    default: {
      throw Error(
        `IMPLEMENTATION ERROR: unhandled compareArea value: ${compareArea}`
      )
    }
  }
}
